import { useEffect, useCallback, useState, useRef } from 'react';

const useGoogleTranslate = () => {
  const [state, setState] = useState({
    isInitialized: false,
    isLoading: false,
    error: null,
    lastAttempt: null
  });
  
  const retryCount = useRef(0);
  const retryTimer = useRef(null);
  const isMounted = useRef(true);
  const initAttempted = useRef(false);
  const verificationTimer = useRef(null);
  const translateElementInstance = useRef(null);

  const setSafeState = (newState) => {
    if (isMounted.current) {
      setState(prev => ({ ...prev, ...newState }));
    }
  };

  useEffect(() => {
    const handleTranslateEvent = () => {
      console.debug('Google Translate completed translation');
      setSafeState({ isLoading: false });
    };
  
    // Listen for Google Translate events
    document.addEventListener('googletranslate', handleTranslateEvent);
  
    return () => {
      document.removeEventListener('googletranslate', handleTranslateEvent);
    };
  }, []);

  // Improved translate widget verification with multiple detection methods
  const verifyTranslateWorks = useCallback(() => {
    try {
      // Method 1: Check for the main combo dropdown
      const comboElement = document.querySelector('.goog-te-combo');
      
      // Method 2: Check for iframe menu frames
      const iframes = document.querySelectorAll('iframe.goog-te-menu-frame');
      
      // Method 3: Check for banner frame
      const banner = document.querySelector('.goog-te-banner-frame');
      
      // Method 4: Check for the TE gadget
      const gadget = document.querySelector('.goog-te-gadget');
      
      // Method 5: Check if any translation cookies exist
      const hasCookie = document.cookie.includes('googtrans');
      
      // Log detection attempts for debugging
      console.debug('Google Translate verification:', {
        comboExists: !!comboElement,
        iframesCount: iframes.length,
        bannerExists: !!banner,
        gadgetExists: !!gadget,
        cookieExists: hasCookie
      });
      
      // If any of these exist, translation might be working
      if (comboElement || iframes.length > 0 || banner || gadget) {
        return true;
      }
      
      return false;
    } catch (error) {
      console.warn('Verification check failed:', error);
      return false;
    }
  }, []);

  const handleRetry = useCallback((retryFn) => {
    if (retryCount.current < 5) { // Increased max retries
      retryCount.current++;
      const delay = Math.min(1000 * Math.pow(1.5, retryCount.current), 10000);
      
      console.debug(`Scheduling retry ${retryCount.current} in ${delay}ms`);
      
      clearTimeout(retryTimer.current);
      retryTimer.current = setTimeout(() => {
        if (isMounted.current) {
          console.debug(`Executing retry ${retryCount.current}`);
          retryFn();
        }
      }, delay);
    } else {
      console.warn('Max retries reached, giving up');
      setSafeState({ 
        isLoading: false,
        error: 'Translation service not available after multiple attempts'
      });
    }
  }, []);

  // Wait and verify initialization with progressive checks
  const waitForInitialization = useCallback((onComplete) => {
    let checkCount = 0;
    const maxChecks = 10;
    
    const checkInitialization = () => {
      checkCount++;
      
      // Clear any previous timer
      clearTimeout(verificationTimer.current);
      
      if (checkCount > maxChecks) {
        console.warn(`Failed to detect Google Translate after ${maxChecks} checks`);
        onComplete(false);
        return;
      }
      
      // Check if initialization worked
      if (verifyTranslateWorks()) {
        console.debug('Google Translate initialization verified');
        onComplete(true);
        return;
      }
      
      // Progressive backoff for checks
      const checkDelay = Math.min(300 * checkCount, 2000);
      
      // Schedule next check with increasing delay
      verificationTimer.current = setTimeout(() => {
        if (isMounted.current) {
          checkInitialization();
        }
      }, checkDelay);
    };
    
    // Start checking
    checkInitialization();
  }, [verifyTranslateWorks]);

  const initializeGoogleTranslate = useCallback(() => {
    // Prevent multiple initialization attempts in quick succession
    if (initAttempted.current) {
      console.debug('Initialization already in progress, skipping');
      return;
    }
    
    initAttempted.current = true;
    console.debug('Starting Google Translate initialization');
    
    // Reset flag after delay
    setTimeout(() => { 
      initAttempted.current = false; 
    }, 3000);
    
    try {
      // Check if Google API exists
      if (!window.google?.translate) {
        console.warn('Google Translate API not loaded');
        setSafeState({ 
          isInitialized: false,
          isLoading: false, // Ensure loading is set to false
          error: 'Google Translate API not loaded',
          lastAttempt: Date.now()
        });
        return;
      }

      // Check if already initialized
      if (verifyTranslateWorks()) {
        console.debug('Google Translate already initialized');
        setSafeState({ 
          isInitialized: true, 
          isLoading: false, // Ensure loading is set to false
          error: null, 
          lastAttempt: Date.now() 
        });
        return;
      }

      // Create a new element each time to avoid stale references
      const elemId = 'google_translate_element_' + Date.now();
      let containerElem = document.getElementById('google_translate_element');
      
      if (!containerElem) {
        containerElem = document.createElement('div');
        containerElem.id = 'google_translate_element';
        containerElem.style.display = 'none';
        document.body.appendChild(containerElem);
      }
      
      // Clear container before adding new element
      containerElem.innerHTML = '';
      const newElement = document.createElement('div');
      newElement.id = elemId;
      containerElem.appendChild(newElement);
      
      try {
        console.debug('Creating TranslateElement instance');
        translateElementInstance.current = new window.google.translate.TranslateElement({
          pageLanguage: 'en',
          autoDisplay: false,
          layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE
        }, elemId);
        
        // Wait and verify initialization with progressive checks
        waitForInitialization((success) => {
          if (success) {
            console.debug('Google Translate initialization successful');
            setSafeState({ 
              isInitialized: true, 
              isLoading: false, // Ensure loading is set to false
              error: null, 
              lastAttempt: Date.now() 
            });
            retryCount.current = 0;
          } else {
            console.warn('Google Translate initialization verification failed');
            setSafeState({ 
              isInitialized: false, 
              isLoading: false, // Ensure loading is set to false
              error: 'Translation service unavailable', 
              lastAttempt: Date.now() 
            });
            
            // Retry with backoff
            handleRetry(initializeGoogleTranslate);
          }
        });
      } catch (initError) {
        console.error('TranslateElement init error:', initError);
        setSafeState({ 
          isInitialized: false,
          isLoading: false, // Ensure loading is set to false
          error: 'Translation service initialization failed', 
          lastAttempt: Date.now() 
        });
        
        // Retry with backoff
        handleRetry(initializeGoogleTranslate);
      }
    } catch (error) {
      console.error('Error initializing Google Translate:', error);
      setSafeState({ 
        isInitialized: false,
        isLoading: false, // Ensure loading is set to false
        error: 'Failed to initialize translation service', 
        lastAttempt: Date.now() 
      });
      
      // Retry with backoff
      handleRetry(initializeGoogleTranslate);
    }
  }, [verifyTranslateWorks, handleRetry, waitForInitialization]);

  const loadScript = useCallback(() => {
    // Prevent loading script if it's already in progress or exists
    if (
      document.getElementById('google-translate-script') || 
      state.isLoading || 
      (state.lastAttempt && Date.now() - state.lastAttempt < 5000)
    ) {
      console.debug('Script loading skipped - already loading or recent attempt');
      return;
    }

    console.debug('Starting Google Translate script load');
    setSafeState({ isLoading: true, error: null });

    try {
      // Remove any previous failed script attempts
      const oldScript = document.getElementById('google-translate-script');
      if (oldScript) {
        oldScript.remove();
      }
      
      const script = document.createElement('script');
      script.id = 'google-translate-script';
      script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateInit';
      script.async = true;
      script.defer = true;
      
      script.onerror = () => {
        console.error('Failed to load Google Translate script');
        setSafeState({ 
          error: 'Translation service unavailable', 
          isLoading: false, // Ensure loading is set to false in error case
          lastAttempt: Date.now()
        });
        
        handleRetry(loadScript);
      };
      
      // Add timeout for script loading
      const scriptTimeout = setTimeout(() => {
        if (state.isLoading && !window.google?.translate) {
          console.error('Script load timeout');
          setSafeState({ 
            isLoading: false, 
            error: 'Translation service load timeout',
            lastAttempt: Date.now()
          });
        }
      }, 10000); // 10 second timeout
      
      // Ensure we don't have duplicate init functions
      if (window.googleTranslateInit) {
        delete window.googleTranslateInit;
      }
      
      window.googleTranslateInit = () => {
        // Wrap in try-catch to prevent uncaught exceptions
        try {
          clearTimeout(scriptTimeout); // Clear timeout on success
          console.debug('Google Translate script loaded, initializing');
          if (isMounted.current) {
            initializeGoogleTranslate();
          }
        } catch (e) {
          console.error('Init callback error:', e);
          setSafeState({ 
            error: 'Translation initialization failed', 
            isLoading: false // Ensure loading is set to false
          });
        }
      };
      
      document.body.appendChild(script);
    } catch (error) {
      console.error('Error loading script:', error);
      setSafeState({ 
        isLoading: false, 
        error: 'Failed to load translation service',
        lastAttempt: Date.now()
      });
    }
  }, [state.isLoading, state.lastAttempt, initializeGoogleTranslate, handleRetry]);

  // Helper function to clear all Google Translate cookies across domains
  const clearAllTranslateCookies = useCallback(() => {
    console.debug('Clearing all Google Translate cookies');
    
    // Get the hostname and split into parts to handle all possible domains
    const hostname = window.location.hostname;
    const domainParts = hostname.split('.');
    
    // Build array of domains to clear cookies from
    const domains = [];
    
    // Add the full domain and all subdomains
    let domainToUse = '';
    for (let i = domainParts.length - 1; i >= 0; i--) {
      if (i === domainParts.length - 1) {
        domainToUse = '.' + domainParts[i];
      } else {
        domainToUse = '.' + domainParts[i] + domainToUse;
      }
      
      if (domainToUse.split('.').length > 2) {
        domains.push(domainToUse);
      }
    }
    
    // Add the exact hostname without leading dot
    domains.push(hostname);
    
    // Add domain with www prefix if not present
    if (!hostname.startsWith('www.')) {
      domains.push(`www.${hostname}`);
      domains.push(`.www.${hostname}`);
    }
    
    // Clear cookies for each domain variant
    domains.forEach(domain => {
      // Clear with path /
      document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`;
      document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      
      // Also clear cookies without specific domain
      document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });
    
    console.debug('Finished clearing Google Translate cookies');
  }, []);

  // Set Google Translate cookie consistently across domains
  const setTranslateCookie = useCallback((langCode) => {
    console.debug(`Setting translation cookie for language: ${langCode}`);
    
    // First clear all existing cookies
    clearAllTranslateCookies();
    
    if (langCode === 'en') {
      // No need to set cookies for English (default)
      return;
    }
    
    // Get the hostname and construct domain variants
    const hostname = window.location.hostname;
    const isWWW = hostname.startsWith('www.');
    const domainWithoutWWW = isWWW ? hostname.substring(4) : hostname;
    
    // Create cookie for the specific domain and path
    document.cookie = `googtrans=/en/${langCode}; path=/; domain=${hostname}`;
    
    // Create cookie for the root domain with leading dot
    document.cookie = `googtrans=/en/${langCode}; path=/; domain=.${domainWithoutWWW}`;
    
    // Create cookie without specific domain
    document.cookie = `googtrans=/en/${langCode}; path=/;`;
    
    // If we're on a www subdomain, also set it without www
    if (isWWW) {
      document.cookie = `googtrans=/en/${langCode}; path=/; domain=${domainWithoutWWW}`;
    }
    
    console.debug('Translation cookies set');
  }, [clearAllTranslateCookies]);

  // Function to properly reset to English
  const resetToEnglish = useCallback(() => {
    console.debug('Resetting to English language');
    
    // Step 1: Clear all cookies to remove any translations
    clearAllTranslateCookies();
    
    // Step 2: Force translation refresh through available methods
    try {
      // Try to recover the original content by triggering a reload of the page content
      const gtElements = document.querySelectorAll('.goog-te-banner-frame, .goog-te-menu-frame');
      gtElements.forEach(el => {
        try {
          if (el && el.contentWindow && el.contentWindow.document) {
            const originalBtn = el.contentWindow.document.querySelector('.goog-te-button button, .goog-te-menu-value');
            if (originalBtn) {
              originalBtn.click();
            }
          }
        } catch (e) {
          console.warn('Could not access frame content:', e);
        }
      });
      
      // Try to use any available combo selectors
      const selectFields = document.querySelectorAll('.goog-te-combo');
      if (selectFields.length > 0) {
        selectFields.forEach(select => {
          try {
            select.value = 'en';
            select.dispatchEvent(new Event('change'));
          } catch (e) {
            console.warn('Error changing combo value:', e);
          }
        });
      }
      
      // If we have a translateElementInstance, use it
      if (translateElementInstance.current && typeof translateElementInstance.current.restore === 'function') {
        console.debug('Using translateElementInstance.restore()');
        translateElementInstance.current.restore();
        return true;
      }
      
      // Fallback - force reload without translations
      document.location.reload();
      
    } catch (error) {
      console.error('Error resetting to English:', error);
      // Last resort - force reload
      document.location.reload();
    }
    
    return true;
  }, [clearAllTranslateCookies]);

  const changeLanguage = useCallback(async (langCode) => {
    console.debug(`Attempting to change language to: ${langCode}`);
    
    try {
      // Special case for English - use dedicated function
      if (langCode === 'en') {
        console.debug('Setting language to English using special method');
        return resetToEnglish();
      }
      
      // Step 1: Always clear all existing translate cookies first
      clearAllTranslateCookies();
  
      // Step 2: Ensure Google Translate is ready
      if (!window.google?.translate) {
        console.debug('Google Translate API not loaded, loading script');
        loadScript();
        throw new Error('Google Translate not yet loaded');
      }
  
      // Step 3: If not initialized, initialize first
      if (!verifyTranslateWorks()) {
        console.debug('Google Translate not initialized, initializing first');
        await initializeGoogleTranslate();
        
        // Add additional wait time for initialization to complete
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
  
      let success = false;
      
      // Approach 1: Using the combo selector
      const selectField = document.querySelector('.goog-te-combo');
      if (selectField) {
        try {
          console.debug('Trying combo selector approach');
          const optionExists = Array.from(selectField.options || []).some(opt => opt.value === langCode);
          if (optionExists) {
            selectField.value = langCode;
            selectField.dispatchEvent(new Event('change'));
            await new Promise(resolve => setTimeout(resolve, 500));
            console.debug('Language changed via combo selector');
            success = true;
          }
        } catch (e) {
          console.warn('Error with combo selector approach:', e);
        }
      }
  
      // If first approach didn't work, try iframe approach
      if (!success) {
        const iframes = document.querySelectorAll('iframe.goog-te-menu-frame');
        if (iframes.length > 0) {
          try {
            console.debug('Trying iframe approach');
            const iframe = iframes[0];
            // Wait for iframe to be ready
            await new Promise(resolve => {
              if (iframe.contentDocument || iframe.contentWindow?.document) {
                resolve();
              } else {
                iframe.onload = resolve;
                setTimeout(resolve, 1000);
              }
            });
  
            const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
            const select = iframeDoc.querySelector('select.goog-te-combo');
            
            if (select) {
              const optionExists = Array.from(select.options || []).some(opt => opt.value === langCode);
              if (optionExists) {
                select.value = langCode;
                select.dispatchEvent(new Event('change'));
                await new Promise(resolve => setTimeout(resolve, 500));
                console.debug('Language changed via iframe selector');
                success = true;
              }
            }
          } catch (e) {
            console.warn('Could not access iframe content:', e);
          }
        }
      }
  
      // Direct cookie approach as fallback
      if (!success) {
        try {
          console.debug('Trying direct cookie approach');
          
          // Set the cookie with consistent format across domains
          setTranslateCookie(langCode);
          
          if (translateElementInstance.current && typeof translateElementInstance.current.update === 'function') {
            // Use our stored instance
            translateElementInstance.current.update();
            await new Promise(resolve => setTimeout(resolve, 500));
            console.debug('Language changed via stored instance update');
            success = true;
          } 
          // If no instance method, try a page refresh
          else {
            document.location.reload();
            return true;
          }
        } catch (e) {
          console.warn('Cookie approach failed:', e);
        }
      }
  
      // Final fallback - force a page reload if all else fails
      if (!success) {
        console.debug('All methods failed, forcing page reload with cookies set');
        setTranslateCookie(langCode); // Set cookies one more time
        window.location.reload();
        return false;
      }
  
      return success;
    } catch (error) {
      console.error('Language change error:', error);
      throw error;
    }
  }, [loadScript, initializeGoogleTranslate, verifyTranslateWorks, clearAllTranslateCookies, setTranslateCookie, resetToEnglish]);

  // Reset service when critical errors occur
  const resetService = useCallback(() => {
    console.debug('Resetting translation service');
    try {
      // Clear all cookies
      clearAllTranslateCookies();
      
      // Remove any Google elements
      const translateElement = document.getElementById('google_translate_element');
      if (translateElement) {
        translateElement.innerHTML = '';
      }
      
      // Remove script if present
      const script = document.getElementById('google-translate-script');
      if (script) script.remove();
      
      // Clear state
      setSafeState({
        isInitialized: false,
        isLoading: false,
        error: null,
        lastAttempt: null
      });
      
      retryCount.current = 0;
      translateElementInstance.current = null;
      clearTimeout(retryTimer.current);
      clearTimeout(verificationTimer.current);
      
      // Reinitialize after a delay
      setTimeout(() => {
        if (isMounted.current) {
          loadScript();
        }
      }, 1000);
    } catch (e) {
      console.error('Error resetting translation service:', e);
      // Ensure loading is set to false even in case of error
      setSafeState({ isLoading: false });
    }
  }, [loadScript, clearAllTranslateCookies]);

  useEffect(() => {
    isMounted.current = true;
    
    // Initial load timer
    const initialLoadTimer = setTimeout(() => {
      if (isMounted.current) {
        if (window.google?.translate) {
          console.debug('Google Translate API found on initial load');
          initializeGoogleTranslate();
        } else {
          console.debug('Loading Google Translate script on initial load');
          loadScript();
        }
      }
    }, 1000);
    
    return () => {
      console.debug('Cleaning up Google Translate hook');
      isMounted.current = false;
      clearTimeout(retryTimer.current);
      clearTimeout(initialLoadTimer);
      clearTimeout(verificationTimer.current);
      
      // Clean up global callback
      if (window.googleTranslateInit) {
        delete window.googleTranslateInit;
      }
    };
  }, [loadScript, initializeGoogleTranslate]);

  return { 
    changeLanguage,
    isInitialized: state.isInitialized,
    isLoading: state.isLoading,
    error: state.error,
    resetService
  };
};

export default useGoogleTranslate;